import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  FileText, Search, RefreshCw, Globe, BookOpen, 
  Shield, ChevronLeft, ChevronRight, Sparkles, Users, Save,
  ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Import centralized language utilities
import { getCountryCodeFromString } from '../utils/languageUtils';

const getCountryCode = getCountryCodeFromString;

const FlagImage = ({ countryCode, className = "w-5 h-4" }) => {
  if (!countryCode) {
    return <Globe className={`${className} text-muted-foreground`} />;
  }
  return (
    <img 
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      alt={countryCode}
      className={`${className} object-cover rounded`}
      loading="lazy"
    />
  );
};

export default function AdminTexts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [texts, setTexts] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [languages, setLanguages] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [togglingPublic, setTogglingPublic] = useState(null);
  const [assigningLanguage, setAssigningLanguage] = useState(null);
  const [unknownTexts, setUnknownTexts] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({}); // { textId: { language_code, is_public } }
  const [savingAll, setSavingAll] = useState(false);
  const [textSort, setTextSort] = useState({ field: null, dir: 'desc' });

  const toggleSort = (field) => {
    setTextSort(prev =>
      prev.field === field
        ? { field, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { field, dir: 'desc' }
    );
  };

  const SortIcon = ({ field }) => {
    if (textSort.field !== field) return <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-40" />;
    return textSort.dir === 'desc'
      ? <ArrowDown className="h-3.5 w-3.5 ml-1" />
      : <ArrowUp className="h-3.5 w-3.5 ml-1" />;
  };

  const sortedTexts = [...texts].sort((a, b) => {
    if (!textSort.field) return 0;
    const dir = textSort.dir === 'desc' ? -1 : 1;
    if (textSort.field === 'word_count') {
      return dir * ((a.word_count || 0) - (b.word_count || 0));
    }
    if (textSort.field === 'created_at') {
      return dir * (new Date(a.created_at || 0) - new Date(b.created_at || 0));
    }
    return 0;
  });

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/languages`, { withCredentials: true });
        setLanguages(response.data || []);
      } catch (err) {
        console.error('Error fetching languages:', err);
      }
    };
    fetchLanguages();
  }, []);

  // Fetch texts
  const fetchTexts = async (search = '', langId = 'all', offset = 0) => {
    try {
      setLoading(true);
      
      // Special handling for unknown language filter
      if (langId === 'unknown') {
        const response = await axios.get(
          `${BACKEND_URL}/api/admin/texts/unknown-language?limit=50&offset=${offset}`,
          { withCredentials: true }
        );
        setUnknownTexts(response.data.texts);
        setTexts([]);
        setPagination({
          total: response.data.total,
          limit: response.data.limit,
          offset: response.data.offset
        });
        return;
      }
      
      setUnknownTexts([]);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (langId && langId !== 'all') params.append('language_id', langId);
      params.append('limit', '50');
      params.append('offset', offset.toString());

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/texts?${params.toString()}`,
        { withCredentials: true }
      );
      
      setTexts(response.data.texts);
      setPagination({
        total: response.data.total,
        limit: response.data.limit,
        offset: response.data.offset
      });
    } catch (err) {
      console.error('Error fetching texts:', err);
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load texts');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/texts/stats`,
        { withCredentials: true }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      fetchTexts();
      fetchStats();
    }
  }, [user]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchTexts(searchQuery, languageFilter, 0);
  };

  // Handle language filter change
  const handleLanguageChange = (value) => {
    setLanguageFilter(value);
    fetchTexts(searchQuery, value, 0);
  };

  // Get languages that actually have texts (from stats)
  const activeLanguages = stats?.texts_by_language?.map(l => ({
    language_id: l.language_id,
    name: l.language_name,
    count: l.count
  })).sort((a, b) => b.count - a.count) || [];

  // Handle toggle public
  const handleTogglePublic = async (textId, currentPublic) => {
    setTogglingPublic(textId);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/texts/${textId}/public?is_public=${!currentPublic}`,
        {},
        { withCredentials: true }
      );
      
      // Update local state
      setTexts(prev => prev.map(t => 
        t.text_id === textId ? { ...t, is_public: !currentPublic } : t
      ));
      
      toast.success(`Text ${!currentPublic ? 'made public' : 'made private'}`);
    } catch (err) {
      console.error('Error toggling public:', err);
      toast.error('Failed to update text');
    } finally {
      setTogglingPublic(null);
    }
  };

  // Handle assign language to a text (queued for batch)
  const handleQueueLanguageChange = (textId, languageCode) => {
    if (!languageCode) return;
    setPendingChanges(prev => ({
      ...prev,
      [textId]: { ...prev[textId], language_code: languageCode }
    }));
  };

  // Handle assign language for unknown texts (immediate)
  const handleAssignLanguage = async (textId, languageCode) => {
    if (!languageCode) return;
    setAssigningLanguage(textId);
    try {
      const response = await axios.patch(
        `${BACKEND_URL}/api/admin/texts/${textId}/language?language_code=${languageCode}`,
        {},
        { withCredentials: true }
      );
      
      const languageName = response.data?.language_name || languageCode;
      toast.success(`Language assigned: ${languageName}`);
      
      // Optimistically remove the text from unknown list immediately
      if (languageFilter === 'unknown') {
        setUnknownTexts(prev => prev.filter(t => t.text_id !== textId));
        setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      }
      
      // Then refresh the list to get fresh data
      setTimeout(() => {
        fetchTexts(searchQuery, languageFilter, pagination.offset);
        fetchStats();
      }, 500);
    } catch (err) {
      console.error('Error assigning language:', err);
      toast.error(err.response?.data?.detail || 'Failed to assign language');
      // Refresh to restore the list on error
      fetchTexts(searchQuery, languageFilter, pagination.offset);
    } finally {
      setAssigningLanguage(null);
    }
  };

  // Batch save all pending changes
  const handleSaveAll = async () => {
    const entries = Object.entries(pendingChanges);
    if (entries.length === 0) {
      toast.info('No pending changes');
      return;
    }
    setSavingAll(true);
    let success = 0;
    let failed = 0;

    for (const [textId, changes] of entries) {
      try {
        if (changes.language_code) {
          await axios.patch(
            `${BACKEND_URL}/api/admin/texts/${textId}/language?language_code=${changes.language_code}`,
            {},
            { withCredentials: true }
          );
        }
        success++;
      } catch (err) {
        console.error(`Failed to update ${textId}:`, err);
        failed++;
      }
    }

    setPendingChanges({});
    toast.success(`Updated ${success} text(s)${failed > 0 ? `, ${failed} failed` : ''}`);
    fetchTexts(searchQuery, languageFilter, pagination.offset);
    fetchStats();
    setSavingAll(false);
  };

  // Discard pending changes
  const handleDiscardChanges = () => {
    setPendingChanges({});
    toast.info('Changes discarded');
  };

  const pendingCount = Object.keys(pendingChanges).length;

  // Pagination
  const handlePrevPage = () => {
    const newOffset = Math.max(0, pagination.offset - pagination.limit);
    fetchTexts(searchQuery, languageFilter, newOffset);
  };

  const handleNextPage = () => {
    const newOffset = pagination.offset + pagination.limit;
    if (newOffset < pagination.total) {
      fetchTexts(searchQuery, languageFilter, newOffset);
    }
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-texts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10">
            <FileText className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Text Management</h1>
            <p className="text-sm text-muted-foreground">Manage all texts in the system</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <>
              <Badge variant="secondary" className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                {pendingCount} pending change{pendingCount > 1 ? 's' : ''}
              </Badge>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDiscardChanges}
                data-testid="discard-btn"
              >
                Discard
              </Button>
              <Button 
                onClick={handleSaveAll}
                disabled={savingAll}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="update-all-btn"
              >
                {savingAll ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Update All ({pendingCount})
              </Button>
            </>
          )}
          <Button 
            variant="outline" 
            onClick={() => { fetchTexts(searchQuery, languageFilter, pagination.offset); fetchStats(); }}
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Total Texts</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.total_texts}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-green-500" />
                <span className="text-sm text-muted-foreground">Public</span>
              </div>
              <p className="text-2xl font-bold mt-1 text-green-500">{stats.public_texts}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-500" />
                <span className="text-sm text-muted-foreground">AI Generated</span>
              </div>
              <p className="text-2xl font-bold mt-1 text-purple-500">{stats.generated_texts}</p>
            </CardContent>
          </Card>
          <Card 
            className={stats.unknown_language_texts > 0 ? 'border-orange-500/50 cursor-pointer hover:bg-orange-500/5' : ''}
            onClick={() => stats.unknown_language_texts > 0 && handleLanguageChange('unknown')}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-orange-500" />
                <span className="text-sm text-muted-foreground">Unknown Language</span>
              </div>
              <p className={`text-2xl font-bold mt-1 ${stats.unknown_language_texts > 0 ? 'text-orange-500' : ''}`}>
                {stats.unknown_language_texts || 0}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Languages */}
      {stats?.texts_by_language?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Texts by Language</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.texts_by_language.map((item, idx) => (
                <Badge 
                  key={idx} 
                  variant="secondary" 
                  className="flex items-center gap-1.5 py-1 px-2"
                >
                  <FlagImage 
                    countryCode={getCountryCode(languages.find(l => l.language_id === item.language_id)?.code)} 
                    className="w-4 h-3"
                  />
                  <span>{item.language_name}</span>
                  <span className="font-bold">{item.count}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filter */}
      <Card>
        <CardContent className="pt-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title..."
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={languageFilter} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="language-filter">
                <SelectValue placeholder="All Languages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Languages</SelectItem>
                {stats?.unknown_language_texts > 0 && (
                  <SelectItem value="unknown" className="text-orange-500">
                    ⚠️ Unknown Language ({stats.unknown_language_texts})
                  </SelectItem>
                )}
                {activeLanguages.map(lang => (
                  <SelectItem key={lang.language_id} value={lang.language_id}>
                    {lang.name} ({lang.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="search-btn">Search</Button>
          </form>
        </CardContent>
      </Card>

      {/* Texts Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Texts ({pagination.total})</CardTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Page {currentPage} of {totalPages || 1}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handlePrevPage} 
                disabled={pagination.offset === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleNextPage} 
                disabled={pagination.offset + pagination.limit >= pagination.total}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : languageFilter === 'unknown' && unknownTexts.length > 0 ? (
            /* Unknown Language Texts Table */
            <div className="overflow-x-auto">
              <div className="mb-4 p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                <p className="text-sm text-orange-500">
                  These texts have no language assigned. Select a language for each text, then click <strong>Update All</strong> to apply.
                </p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[250px]">Title</TableHead>
                    <TableHead className="min-w-[150px]">Content Preview</TableHead>
                    <TableHead className="text-center">Words</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="min-w-[180px]">Assign Language</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unknownTexts.map((text) => {
                    const pending = pendingChanges[text.text_id];
                    const pendingLang = pending?.language_code;
                    const pendingLangName = pendingLang ? languages.find(l => l.code === pendingLang)?.name : null;
                    return (
                    <TableRow 
                      key={text.text_id} 
                      className={pending ? 'bg-amber-500/5 border-amber-500/20' : ''}
                      data-testid={`unknown-text-${text.text_id}`}
                    >
                      <TableCell>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate max-w-[200px]" title={text.title}>
                              {text.title || 'Untitled'}
                            </span>
                            {pending && (
                              <Badge variant="outline" className="text-amber-400 border-amber-400/50 text-xs">
                                {pendingLangName}
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{text.text_id}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground line-clamp-2">
                          {text.content_preview}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium">{text.word_count?.toLocaleString() || 0}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm truncate max-w-[100px]" title={text.user_email}>
                          {text.user_email}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={pendingLang || ''}
                          onValueChange={(val) => handleQueueLanguageChange(text.text_id, val)}
                        >
                          <SelectTrigger className="w-[160px]" data-testid={`assign-lang-${text.text_id}`}>
                            <SelectValue placeholder="Select language" />
                          </SelectTrigger>
                          <SelectContent>
                            {languages.map(lang => (
                              <SelectItem key={lang.language_id} value={lang.code}>
                                {lang.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : texts.length === 0 && unknownTexts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No texts found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[250px]">Title</TableHead>
                    <TableHead>Language</TableHead>
                    <TableHead className="min-w-[180px]">Change Language</TableHead>
                    <TableHead
                      className="text-center cursor-pointer select-none hover:text-foreground"
                      onClick={() => toggleSort('word_count')}
                      data-testid="sort-words-header"
                    >
                      <span className="inline-flex items-center justify-center">
                        Words <SortIcon field="word_count" />
                      </span>
                    </TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead
                      className="cursor-pointer select-none hover:text-foreground"
                      onClick={() => toggleSort('created_at')}
                      data-testid="sort-created-header"
                    >
                      <span className="inline-flex items-center">
                        Created <SortIcon field="created_at" />
                      </span>
                    </TableHead>
                    <TableHead className="text-center">Public</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTexts.map((text) => {
                    const pending = pendingChanges[text.text_id];
                    const pendingLang = pending?.language_code;
                    const pendingLangName = pendingLang ? languages.find(l => l.code === pendingLang)?.name : null;
                    return (
                    <TableRow 
                      key={text.text_id} 
                      className={pending ? 'bg-amber-500/5 border-amber-500/20' : ''}
                      data-testid={`text-row-${text.text_id}`}
                    >
                      <TableCell>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate max-w-[200px]" title={text.title}>
                              {text.title || 'Untitled'}
                            </span>
                            {text.is_generated && (
                              <Badge variant="outline" className="text-purple-500 border-purple-500/50 text-xs">
                                <Sparkles className="h-3 w-3 mr-1" />
                                AI
                              </Badge>
                            )}
                            {pending && (
                              <Badge variant="outline" className="text-amber-400 border-amber-400/50 text-xs">
                                modified
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{text.text_id}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FlagImage 
                            countryCode={getCountryCode(pendingLang || text.language_code)} 
                            className="w-5 h-4"
                          />
                          <span className={`text-sm ${pendingLang ? 'line-through text-muted-foreground' : ''}`}>
                            {text.language_name}
                          </span>
                          {pendingLangName && (
                            <span className="text-sm text-amber-400 font-medium">{pendingLangName}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={pendingLang || ''}
                          onValueChange={(val) => handleQueueLanguageChange(text.text_id, val)}
                        >
                          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid={`change-lang-${text.text_id}`}>
                            <SelectValue placeholder="Change..." />
                          </SelectTrigger>
                          <SelectContent>
                            {languages.map(lang => (
                              <SelectItem key={lang.language_id} value={lang.code}>
                                {lang.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center">
                          <span className="font-medium">{text.word_count?.toLocaleString() || 0}</span>
                          <span className="text-xs text-muted-foreground">
                            {text.unique_words} unique
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm">{text.user_name || 'Unknown'}</span>
                          <span className="text-xs text-muted-foreground truncate max-w-[120px]" title={text.user_email}>
                            {text.user_email}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(text.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={text.is_public || false}
                          onCheckedChange={() => handleTogglePublic(text.text_id, text.is_public)}
                          disabled={togglingPublic === text.text_id}
                          data-testid={`toggle-public-${text.text_id}`}
                        />
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
